import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { ApWindowService } from '@ap/cdk';
import {
    AiConsentParams,
    AutoTopUpQueryParams,
    BalanceChartParams,
    BetaalverzoekDetails,
    BudgetCreateParams,
    BudgetParams,
    HypothekenDocumentupload,
    IncassobatchesUitvalrapportagesDetails,
    InkomstenUitgavenCategorieDetails,
    InkomstenUitgavenDetails,
    MijnNavigationEvents,
    NotificatiesBeherenParams,
    OptionalAccountDetails,
    OverboekenQueryParams,
    PathParam,
    ProductAanvraag,
    RekeningDetails,
    SelectedAccountDetails,
    ToegangsnaamWachtwoordAanvraag,
    TransactiesDownloadenDetails
} from '@ap/mijn-navigatie';

import { ContactgegevensQueryParams } from './phonenumber-validation/phonenumber-validation.model';

/**
 * @module MijnOmgevingModule
 * @ngdoc service
 * @name MijnOmgevingNavigation
 * @description This service facilitates navigation between processes in the mijn-omgeving.
 * It should be the ONLY service that does this.
 * Every process can add a way to reach them in this service.
 */
@Injectable()
export class MijnOmgevingNavigation implements MijnNavigationEvents {
    private currentUrl: string;
    private previousUrl: string;

    public constructor(
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly windowService: ApWindowService
    ) {}

    public toAsnMigrationOnboarding(): Promise<boolean> {
        return Promise.resolve(false);
    }

    public toAflosvrijCheck(): Promise<boolean> {
        return this.internal('aflosvrij-check');
    }

    public toAiConsent(aiConsentParams?: AiConsentParams): Promise<boolean> {
        return this.internal('zelf-regelen/privacy/toestemming-inzicht-in-je-geld', aiConsentParams);
    }

    public toDigipasDeblokkeren(): Promise<boolean> {
        return this.internal('zelf-regelen/digipas-deblokkeren');
    }

    public toZakelijkDigipasVervangen(): Promise<boolean> {
        return this.internal('zakelijk-digipas-vervangen');
    }

    public toDigitaalAdresWijzigen(): Promise<boolean> {
        return this.internal('digitaal-adres-wijzigen');
    }

    public toOpenstaandeActies(): Promise<boolean> {
        return this.internal('zelf-regelen/openstaande-acties');
    }

    public toAdresboek(): Promise<boolean> {
        return this.internal('adresboek');
    }

    public toAfsprakenbeheer(): Promise<boolean> {
        return this.internal('contact/afspraken');
    }

    public toAlerts(notificatiesBeherenParam?: NotificatiesBeherenParams): Promise<boolean> {
        return this.internal('zelf-regelen/notificaties-beheren/seintjes', notificatiesBeherenParam);
    }

    public toAlertsSecurity(): Promise<boolean> {
        return this.internal('zelf-regelen/notificaties-beheren/veiligheids-berichten');
    }

    public toAutomatiseringenBeheren(): Promise<boolean> {
        return this.internal('zelf-regelen/automatiseringen-beheren');
    }

    public toBericht(pathParam: PathParam): Promise<boolean> {
        return this.internal(`berichten/${pathParam.path}`);
    }

    public toBerichtvoorkeuren(): Promise<boolean> {
        return this.internal('zelf-regelen/berichtvoorkeuren');
    }

    public toBerichtvoorkeurenNba(): Promise<boolean> {
        return this.internal('zelf-regelen/berichtvoorkeuren/nba');
    }

    public toBetaalpassen(): Promise<boolean> {
        return this.internal('zelf-regelen/betaalpassen');
    }

    public toBouwdepotAdresNieuweWoningDoorgeven(): Promise<boolean> {
        return this.internal('hypotheek/bouwdepot/adres-nieuwe-woning-doorgeven');
    }

    public toBouwdepotBeeindigen(): Promise<boolean> {
        return this.internal('hypotheek/bouwdepot/beeindigen');
    }

    public toBouwdepotDeclaratie(): Promise<boolean> {
        return this.internal('hypotheek/bouwdepot/declaratie');
    }

    public toContactTelefonie(): Promise<boolean> {
        return this.internal('contact/contact-telefonie');
    }

    public toEcommerce(pathParam?: PathParam): Promise<boolean> {
        if (pathParam) {
            return this.internal(`zelf-regelen/ecommerce/${pathParam.path}`);
        }
        return this.internal(`zelf-regelen/ecommerce`);
    }

    public toEcommerceSummary(): Promise<boolean> {
        return this.internal('zelf-regelen/ecommerce/overzicht');
    }

    public toExterneBetaling(): Promise<boolean> {
        return this.internal('externe-betaling');
    }

    public toGebruikersnaamWachtwoordWijzigen(pathParam: PathParam): Promise<boolean> {
        return this.internal(`zelf-regelen/gebruikersnaam-wachtwoord-wijzigen/${pathParam.path}`);
    }

    public toGeldzakenKind(): Promise<boolean> {
        return this.internal('zelf-regelen/geldzaken-kind');
    }

    public toHeridentificatie(): Promise<boolean> {
        return this.internal('zelf-regelen/heridentificatie');
    }

    public toHuismap(): Promise<boolean> {
        return this.internal('huismap');
    }

    public toHypothekenDocumentenUploaden(): Promise<boolean> {
        return this.internal('hypotheken-documenten-uploaden');
    }

    public toHypothekenAflossingsvrij(): Promise<boolean> {
        return this.internal('hypotheek/hypotheken-aflossingsvrij');
    }

    public toHypothekenAflossingsvrijOmzetten(): Promise<boolean> {
        return this.internal('hypotheek/hypotheken-aflossingsvrij-omzetten');
    }

    public toHypothekenDocumentupload(pathParam: HypothekenDocumentupload): Promise<boolean> {
        const { path, ...queryParams } = pathParam;
        return this.internal(`hypotheek/hypotheken-documentupload/${path}`, queryParams);
    }

    public toHypothekenIncassorekening(): Promise<boolean> {
        return this.internal('hypotheek/hypotheken-incassorekening');
    }

    public toHypothekenAflossen(): Promise<boolean> {
        return this.internal('hypotheek/hypotheken-aflossen');
    }

    public toHypothekenWoningwaarde(): Promise<boolean> {
        return this.internal('hypotheek/hypotheken-woningwaarde');
    }

    public toHypothekenVerhogen(): Promise<boolean> {
        return this.internal('hypotheek/hypotheken-verhogen');
    }

    public toHypothekenVerhogenAanvraag(): Promise<boolean> {
        return this.internal('hypotheek/hypotheken-verhogen/aanvraag');
    }

    public toHypothekenVerhogenKennistoets(): Promise<boolean> {
        return this.internal('hypotheek/hypotheken-verhogen/kennistoets');
    }

    public toIdin(): Promise<boolean> {
        return this.internal('zelf-regelen/idin');
    }

    public toIdealZelfservice(): Promise<boolean> {
        return this.internal('zelf-regelen/ideal');
    }

    public toIdealZelfserviceError(): Promise<boolean> {
        return this.internal('zelf-regelen/ideal/fout');
    }

    public toIdealZelfserviceDetails(): Promise<boolean> {
        return this.internal('zelf-regelen/ideal/details');
    }

    public toIdealZelfserviceProfielAanmaken(): Promise<boolean> {
        return this.internal('zelf-regelen/ideal/profiel-aanmaken');
    }

    public toIncasso(selectedAccountDetails?: SelectedAccountDetails): Promise<boolean> {
        if (selectedAccountDetails) {
            return this.internal('incasso', selectedAccountDetails);
        }

        return this.internal('incasso');
    }

    public toIncassoAddPermission(): Promise<boolean> {
        return this.internal('incasso/toestemming-toevoegen');
    }

    public toIncassoChangePermissionList(): Promise<boolean> {
        return this.internal('incasso/toestemmingslijst-wijzigen');
    }

    public toIncassoRefund(): Promise<boolean> {
        return this.internal('incasso/incasso-terugboeken');
    }

    public toIncassoRefuse(): Promise<boolean> {
        return this.internal('incasso/incasso-weigeren');
    }

    public toIncassoRemovePermission(): Promise<boolean> {
        return this.internal('incasso/toestemming-verwijderen');
    }

    public toIncassoUpdatePermission(): Promise<boolean> {
        return this.internal('incasso/toestemming-updaten');
    }

    public toIncassobatches(): Promise<boolean> {
        return this.internal('incassobatches');
    }

    public toIncassobatchesDetails(): Promise<boolean> {
        return this.internal('incassobatches/details');
    }

    public toIncassobatchesError(): Promise<boolean> {
        return this.internal('incassobatches/fout');
    }

    public toIncassobatchesSign(): Promise<boolean> {
        return this.internal('incassobatches/sign');
    }

    public toIncassobatchesSuccess(): Promise<boolean> {
        return this.internal('incassobatches/succes');
    }

    public toIncassobatchesUpload(): Promise<boolean> {
        return this.internal('incassobatches/upload');
    }

    public toIncassobatchesUitvalrapportages(
        incassobatchesUitvalrapportagesDetails?: IncassobatchesUitvalrapportagesDetails
    ): Promise<boolean> {
        const url = 'incassobatches/uitvalrapportages';

        if (incassobatchesUitvalrapportagesDetails) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { path, ...queryParams } = incassobatchesUitvalrapportagesDetails;
            if (Object.keys(queryParams).length > 0) {
                return this.internal(url, queryParams);
            }
        }

        return this.internal(url);
    }

    public toIncassoNotificatie(): Promise<boolean> {
        return this.internal('zelf-regelen/notificaties-beheren/incasso-notificatie');
    }

    public toIngeplandeOverboeking(optionalAccountDetails?: OptionalAccountDetails): Promise<boolean> {
        if (optionalAccountDetails) {
            const { path, ...queryParams } = optionalAccountDetails;
            const url = path ? `ingeplande-overboeking/${path}` : 'ingeplande-overboeking';

            if (Object.keys(queryParams).length > 0) {
                return this.internal(url, queryParams);
            }

            return this.internal(url);
        }

        return this.internal(`ingeplande-overboeking`);
    }

    public toIngeplandGeweigerdNotificatie(pathParam: PathParam): Promise<boolean> {
        return this.internal(`zelf-regelen/ingepland-geweigerd-notificatie/${pathParam.path}`);
    }

    public toIngeplandGeweigerdNotificaties(): Promise<boolean> {
        return this.internal('zelf-regelen/ingepland-geweigerd-notificatie');
    }

    public toNotificatiesBeheren(notificatiesBeherenParam?: NotificatiesBeherenParams): Promise<boolean> {
        return this.internal('zelf-regelen/notificaties-beheren', notificatiesBeherenParam);
    }

    public toNotificatiesEnSeintjes(): Promise<boolean> {
        return this.internal('zelf-regelen/notificaties-en-seintjes');
    }

    public toOpgehevenRekeningen(): Promise<boolean> {
        return this.internal('zelf-regelen/opgeheven-rekeningen');
    }

    public toOrder(pathParam: PathParam): Promise<boolean> {
        return this.internal(`beleggen/${pathParam.path}/order`);
    }

    public toOrderHistory(pathParam: PathParam): Promise<boolean> {
        return this.internal(`beleggen/${pathParam.path}/order-history`);
    }

    public toOrderOverzicht(pathParam: PathParam): Promise<boolean> {
        return this.internal(`beleggen/${pathParam.path}/order-overzicht`);
    }

    public toFinancialReturn(pathParam: PathParam): Promise<boolean> {
        return this.internal(`beleggen/${pathParam.path}/financieel-rendement`);
    }

    public toPendingPayments(): Promise<boolean> {
        return this.internal(`verzendlijst`);
    }

    public toPensionExpansionRequestInvestment(): Promise<boolean> {
        return this.internal(`pensioen-uitbreiding-aanvragen/beleggen`);
    }

    public toPensionExpansionRequestDeposit(): Promise<boolean> {
        return this.internal(`pensioen-uitbreiding-aanvragen/deposito`);
    }

    public toPortfolio(pathParam: PathParam): Promise<boolean> {
        return this.internal(`beleggen/${pathParam.path}/portefeuille`);
    }
    public toCash(pathParam: PathParam): Promise<boolean> {
        return this.internal(`beleggen/${pathParam.path}/gelddeel`);
    }

    public toSustainableReturn(pathParam: PathParam): Promise<boolean> {
        return this.internal(`beleggen/${pathParam.path}/duurzaam-rendement`);
    }

    public toPlannerOverview(pathParam: PathParam): Promise<boolean> {
        return this.internal(`beleggen/${pathParam.path}/planner-overzicht`);
    }

    public toPlanner(pathParam: PathParam): Promise<boolean> {
        return this.internal(`beleggen/${pathParam.path}/planner`);
    }

    public toOrders(pathParam: PathParam): Promise<boolean> {
        return this.internal(`beleggen/${pathParam.path}/orders`);
    }

    public toBeleggenBeheer(pathParam: PathParam): Promise<boolean> {
        return this.internal(`beleggen/${pathParam.path}/beheer`);
    }

    public toBeleggenCancelAccount(pathParam: PathParam): Promise<boolean> {
        return this.internal(`beleggen/${pathParam.path}/rekening-opheffen`);
    }

    public toBeleggenComplete(pathParam: PathParam): Promise<boolean> {
        return this.internal(`beleggen/${pathParam.path}/resultaat`);
    }

    public toOrderAllocation(pathParam: PathParam): Promise<boolean> {
        return this.internal(`beleggen/${pathParam.path}/automatisch-aankopen`);
    }

    public toBeleggenSigneren(pathParam: PathParam): Promise<boolean> {
        return this.internal(`beleggen/${pathParam.path}/signeren`);
    }

    public toManageAccess(): Promise<boolean> {
        return this.internal('zelf-regelen/persoon-toevoegen-verwijderen');
    }

    public toPrivacy(): Promise<boolean> {
        return this.internal('zelf-regelen/privacy');
    }

    public toProductToevoegen(): Promise<boolean> {
        return this.internal('zelf-regelen/product-toevoegen');
    }

    public toProductCatalog(): Promise<boolean> {
        return this.internal('zelf-regelen/product-toevoegen');
    }

    public toRenteRegulierVerlengen(): Promise<boolean> {
        return this.internal('hypotheek/rente/regulier-verlengen');
    }

    public toRenteTussentijdsAanpassen(): Promise<boolean> {
        return this.internal('hypotheek/rente/tussentijds-aanpassen');
    }

    public toSaldoNotificatie(pathParam: PathParam): Promise<boolean> {
        return this.internal(`zelf-regelen/notificaties-beheren/saldo-notificatie/${pathParam.path}`);
    }

    public toSaldoNotificaties(): Promise<boolean> {
        return this.internal('zelf-regelen/notificaties-beheren/saldo-notificatie');
    }

    public toToegangDerdenBeheren(): Promise<boolean> {
        return this.internal('zelf-regelen/toegang-derden-beheren');
    }

    public toToegangGegevensDelen(): Promise<boolean> {
        return this.internal('zelf-regelen/toegang-gegevens-delen');
    }

    public toToegangsnaamWachtwoordAanvragen(queryParams?: ToegangsnaamWachtwoordAanvraag): Promise<boolean> {
        if (queryParams) {
            return this.internal(`zelf-regelen/toegangsnaam-wachtwoord-aanvragen`, queryParams);
        }
        return this.internal(`zelf-regelen/toegangsnaam-wachtwoord-aanvragen`);
    }

    public toTransactieNotificatie(pathParam: PathParam): Promise<boolean> {
        return this.internal(`zelf-regelen/notificaties-beheren/transactie-notificatie/${pathParam.path}`);
    }

    public toTransactieNotificaties(): Promise<boolean> {
        return this.internal('zelf-regelen/notificaties-beheren/transactie-notificatie');
    }

    public toTransactiesDownloaden(transactiesDownloadenDetails?: TransactiesDownloadenDetails): Promise<boolean> {
        return this.internal('zelf-regelen/transacties-downloaden', transactiesDownloadenDetails);
    }

    public toRekeningstandenDownloaden(): Promise<boolean> {
        return this.internal('zelf-regelen/rekeningstanden-downloaden');
    }

    public toOnlineBankierenKind(): Promise<boolean> {
        return this.internal('zelf-regelen/online-bankieren-kind');
    }

    public toVeiligheidEnToegang(): Promise<boolean> {
        return this.internal('zelf-regelen/veiligheid-en-toegang');
    }

    public toVerzekerenBeheren(): Promise<boolean> {
        return this.internal('verzekeren-beheren');
    }

    public toVerzekerenDetails(pathParam: PathParam): Promise<boolean> {
        return this.internal(`verzekeren-beheren/details/${pathParam.path}`);
    }

    public toVerzekerenProductAddition(pathParam: PathParam): Promise<boolean> {
        return this.internal(`verzekeren-beheren/add/${pathParam.path}`);
    }

    public toVerzekerenProductPage(pathParam: PathParam): Promise<boolean> {
        return this.internal(`verzekeren-beheren/product-overview/${pathParam.path}`);
    }

    public toVerzekerenMaintenance(): Promise<boolean> {
        return this.internal(`verzekeren-beheren/onderhoud`);
    }

    public toVoorkeursrekeningBeheren(): Promise<boolean> {
        return this.internal('zelf-regelen/voorkeursrekening-beheren');
    }

    public toWeergaveVoorkeuren(): Promise<boolean> {
        return this.internal('zelf-regelen/weergavevoorkeuren');
    }

    public toWijzigenAfschriftfrequentie(): Promise<boolean> {
        return this.internal('wijzigen-afschriftfrequentie');
    }

    public toZelfRegelenHypotheken(): Promise<boolean> {
        return this.internal('zelf-regelen/hypotheken');
    }

    public toZelfRegelenRekeningen(): Promise<boolean> {
        return this.internal('zelf-regelen/rekeningen');
    }

    public toDocumenten(): Promise<boolean> {
        return this.internal('documenten');
    }

    public toHomepage(queryParams?: Params): Promise<boolean> {
        return this.internal('', queryParams).then((result) => {
            if (queryParams && queryParams['reload']) {
                window.location.reload();
            }
            return result;
        });
    }

    public toAccountsOverview(): Promise<boolean> {
        return this.internal('rekeningen-overzicht');
    }

    public toOverviewEdit(): Promise<boolean> {
        return this.internal('overzicht-aanpassen');
    }

    public toProductAanvragen(queryParams?: ProductAanvraag): Promise<boolean> {
        if (queryParams) {
            return this.internal(`zelf-regelen/product-aanvragen`, queryParams);
        }
        return this.internal(`zelf-regelen/product-aanvragen`);
    }

    public toOverboeken(overboekenQueryParams?: OverboekenQueryParams): Promise<boolean> {
        if (overboekenQueryParams) {
            return this.internal('overboeken', overboekenQueryParams);
        }
        return this.internal('overboeken');
    }

    public toVerzendlijst(): Promise<boolean> {
        return this.internal('overboeken/verzendlijst');
    }

    public toOverboekenOndertekenen(): Promise<boolean> {
        return this.internal('overboeken/ondertekenen');
    }

    public toOverboekenOverzicht(): Promise<boolean> {
        return this.internal('overboeken/overzicht');
    }

    public toOverboekenSpaarpot(): Promise<boolean> {
        return this.internal('overboeken/spaarpot');
    }

    public toBetaalbatches(): Promise<boolean> {
        return this.internal('betaalbatches');
    }

    public toBetaalbatchesAfgerondBestand(): Promise<boolean> {
        return this.internal('betaalbatches/afgerond-bestand');
    }

    public toBetaalbatchesBatchdetails(): Promise<boolean> {
        return this.internal('betaalbatches/batch-details');
    }

    public toBetaalbatchesBatchbestanddetails(): Promise<boolean> {
        return this.internal('betaalbatches/bestand-details');
    }

    public toBetaalbatchesSign(): Promise<boolean> {
        return this.internal('betaalbatches/sign');
    }

    public toBetaalbatchesResult(): Promise<boolean> {
        return this.internal('betaalbatches/result');
    }

    public toBetaalbatchesUpload(): Promise<boolean> {
        return this.internal('betaalbatches/upload');
    }

    public toNewBetaalverzoek(betaalverzoekDetails?: BetaalverzoekDetails): Promise<boolean> {
        if (betaalverzoekDetails) {
            const params: Params = { rekening: betaalverzoekDetails?.rekening, transactie_id: betaalverzoekDetails?.transactie_id };
            return this.internal('betaalverzoek/betaalverzoek-aanmaken', params);
        }
        return this.internal('betaalverzoek/betaalverzoek-aanmaken');
    }

    public toBetaalverzoek(): Promise<boolean> {
        return this.internal('betaalverzoek');
    }

    public toAutoTopUp(autoTopUpQueryParams?: AutoTopUpQueryParams): Promise<boolean> {
        if (autoTopUpQueryParams) {
            return this.internal('betaalrekening-aanvullen', autoTopUpQueryParams);
        }

        return this.internal('betaalrekening-aanvullen');
    }

    public toBerichten(): Promise<boolean> {
        return this.internal('berichten');
    }

    public toContact(): Promise<boolean> {
        return this.internal('contact');
    }

    public toHelaas(): Promise<boolean> {
        return this.internal('helaas');
    }

    public toFout(): Promise<boolean> {
        return this.internal('fout');
    }

    public toHypotheek(): Promise<boolean> {
        return this.internal('hypotheek');
    }

    public toHypotheekDetails(): Promise<boolean> {
        return this.internal('hypotheek/details');
    }

    public toHypotheekEnergylabel(): Promise<boolean> {
        return this.internal('hypotheek/energie-label');
    }

    public toHypothekenExtraStorten(): Promise<boolean> {
        return this.internal('hypotheek/hypotheken-extra-storten');
    }

    public toVorigeHypotheekAfschrijvingen(): Promise<boolean> {
        return this.internal('hypotheek/vorige-afschrijvingen');
    }

    public toVolgendeHypotheekAfschrijving(): Promise<boolean> {
        return this.internal('hypotheek/volgende-afschrijving');
    }

    public toTokenBeheer(): Promise<boolean> {
        return this.internal('zelf-regelen/inlogmiddelen-beheren');
    }

    public toDigipasVerwijderen(pathParam: PathParam): Promise<boolean> {
        return this.internal(`zelf-regelen/inlogmiddelen-beheren/digipas-verwijderen/${pathParam.path}`);
    }

    public toContactgegevensBeheren(queryParams?: ContactgegevensQueryParams): Promise<boolean> {
        if (queryParams) {
            return this.internal('zelf-regelen/contactgegevens-beheren', queryParams);
        }
        return this.internal('zelf-regelen/contactgegevens-beheren');
    }

    public toLimieten(): Promise<boolean> {
        return this.internal('zelf-regelen/limieten-beheren');
    }

    public toAuthenticatieHistorie(): Promise<boolean> {
        return this.internal('zelf-regelen/authenticatie-historie');
    }

    public toGeweigerd(optionalAccountDetails?: OptionalAccountDetails): Promise<boolean> {
        if (optionalAccountDetails) {
            const { path, ...queryParams } = optionalAccountDetails;
            const url = path ? `zelf-regelen/geweigerde-overboekingen/${path}` : 'zelf-regelen/geweigerde-overboekingen';

            if (Object.keys(queryParams).length > 0) {
                return this.internal(url, queryParams);
            }

            return this.internal(url);
        }

        return this.internal(`zelf-regelen/geweigerde-overboekingen`);
    }

    public toPaginaNietGevonden(): Promise<boolean> {
        return this.internal('pagina-niet-gevonden');
    }

    public toRekening(rekeningDetails: RekeningDetails): Promise<boolean> {
        if (rekeningDetails) {
            const { path, ...queryParams } = rekeningDetails;
            if (Object.keys(queryParams).length > 0) {
                return this.internal(`rekening/${path}`, queryParams);
            }
            return this.internal(`rekening/${path}`);
        }
        return this.toPaginaNietGevonden();
    }

    public toBeleggen(pathParam: PathParam): Promise<boolean> {
        if (typeof pathParam === 'string') {
            // Deprecated use of string as pathParam
            return this.internal(`beleggen/${pathParam}`);
        } else if (pathParam) {
            return this.internal(`beleggen/${pathParam.path}`);
        }
        return this.internal('beleggen');
    }

    public toLenen(): Promise<boolean> {
        return this.internal('leningen-aanvragen');
    }

    public toLenenBeheer(): Promise<boolean> {
        return this.internal('leningen-aanvragen/beheer');
    }

    public toPersoonlijkeLening(pathParam: PathParam): Promise<boolean> {
        if (pathParam) {
            return this.internal(`persoonlijke-lening/${pathParam.path}`);
        }
        return this.internal(`persoonlijke-lening`);
    }

    public toSecurityAwareness(): Promise<boolean> {
        return this.internal('security-awareness-training');
    }

    public toInkomstenUitgaven(inkomstenUitgavenDetails?: InkomstenUitgavenDetails): Promise<boolean> {
        return this.internal('inkomsten-en-uitgaven', inkomstenUitgavenDetails);
    }

    public toBalanceChart(balanceChartParams?: BalanceChartParams): Promise<boolean> {
        return this.internal('saldografiek', balanceChartParams);
    }

    public toBudgets(): Promise<boolean> {
        return this.internal('budgetten');
    }

    public toBudgetCreate(budgetCreateParams?: BudgetCreateParams): Promise<boolean> {
        return this.internal('budgetten/instellen', budgetCreateParams);
    }

    public toBudgetDetails(budgetParams: BudgetParams): Promise<boolean> {
        if (budgetParams) {
            return this.internal('budgetten/details', budgetParams);
        }
        return this.toPaginaNietGevonden();
    }

    public toBudgetEdit(budgetParams: BudgetParams): Promise<boolean> {
        if (budgetParams) {
            return this.internal('budgetten/aanpassen', budgetParams);
        }
        return this.toPaginaNietGevonden();
    }

    public toFixedExpenses(): Promise<boolean> {
        return this.internal('vaste-lasten');
    }

    public toInkomstenUitgavenCategorie(inkomstenUitgavenCategorieDetails?: InkomstenUitgavenCategorieDetails): Promise<boolean> {
        return this.internal('inkomsten-en-uitgaven/categorie', inkomstenUitgavenCategorieDetails);
    }

    public toTelefoonnummerValidatie(): Promise<boolean> {
        return this.internal('telefoonnummer-validatie');
    }

    public toSpaarpot(pathParam: PathParam): Promise<boolean> {
        if (typeof pathParam === 'string') {
            // Deprecated use of string as pathParam
            return this.internal(`spaarpot/${pathParam}`);
        } else if (pathParam) {
            return this.internal(`spaarpot/${pathParam.path}`);
        }
        return this.toPaginaNietGevonden();
    }

    public toTelephonyConsent(): Promise<boolean> {
        return this.internal('zelf-regelen/privacy/toestemming-telefonie');
    }

    public toZakelijkGeblokkeerd(): Promise<boolean> {
        return this.internal('zakelijk-geblokkeerd');
    }

    public toZakelijkExtraAflossen(): Promise<boolean> {
        return this.internal('zakelijk-extra-aflossen');
    }

    public toZelfRegelen(): Promise<boolean> {
        return this.internal('zelf-regelen');
    }

    public internal(path: string, queryParams?: Params): Promise<boolean> {
        return this.router.navigate([path], {
            relativeTo: this.activatedRoute,
            queryParams
        });
    }

    public external(href: string): void {
        this.windowService.location_href(href);
    }

    public setCurrentUrl(url: string): void {
        this.previousUrl = this.currentUrl;
        this.currentUrl = url;
    }

    public getPreviousUrl(): string {
        return this.previousUrl ? this.previousUrl : '';
    }

    public getCurrentUrl(): string {
        return this.currentUrl;
    }
}
